/**
 * Nástenka — každý si ju poskladá sám.
 *
 * Oto: *„Poďme trošku vymakať dashboard. Dať tam nejaké používanejšie veci,
 * nejaký výcuc, a aby si každý užívateľ mohol aj nejaké veci pridať, ubrať.
 * Spravme ho celý taký sofistikovanejší."*
 *
 * ── Ako je to postavené ──────────────────────────────────────────────────
 * Obrazovka nevie, čo je na nástenke — pýta sa servera. Ten vráti **zoznam
 * kariet, na ktoré má človek právo**, jeho vlastný výber a čísla len pre tie
 * vybrané. Tu je z toho už len mapa `kľúč → komponent`.
 *
 * Preto pridať novú kartu znamená dopísať jednu položku v registri na serveri
 * a jeden komponent sem. Nikde inde sa nič nemení.
 *
 * ── Prečo úprava nie je „ťahanie myšou" ──────────────────────────────────
 * Ťahanie vyzerá lepšie a na dotykovom displeji pri baliacom stole sa s ním
 * nedá trafiť. Šípky hore/dole fungujú všade a dajú sa stlačiť aj v rukavici.
 *
 * ── .888 Nový vzhľad ─────────────────────────────────────────────────────
 * Oto: „oveľa lepšiu, vymakanú grafiku na úvodný prehľad". Len vzhľad –
 * dáta, odkazy, práva aj texty ostali. Pribudla farebná hlavička s pozdravom
 * a hlavnými číslami, pás „Treba riešiť" poskladaný z čísel, ktoré nástenka
 * už má, a farebné dlaždice ako v Lead managemente a ESHOP-e. Farba nesie
 * význam: emerald hotové/dobré, amber čaká, sky rozpracované, rose problém,
 * violet peniaze, slate neutrálne – a prázdna (nulová) dlaždica je vždy sivá.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, AlertCircle, TrendingUp, Wrench, FileSignature,
  Truck, PackageCheck, Inbox, PackagePlus, BarChart3, Percent, Coins, Package,
  LayoutDashboard, Plus, X, ChevronUp, ChevronDown, RotateCcw, Check, RefreshCw,
  CalendarClock, AlertTriangle, CheckCircle2, Target, Users, ArrowUpRight, Sparkles,
  Wallet, FileText, ChevronRight, Phone, Mail, UserPlus, Search, BookOpen, FlaskConical,
  ClipboardList, FilePlus2, Eye, Receipt,
} from 'lucide-react';
import { api, money, dateSk, dtSk, today, CAS_PASMO } from '../api';
import { Loading, useToast, StatusBadge } from '../components/ui';
import DenKalendar from '../components/DenKalendar';
import { casSk as casPredvedenia } from '../components/PredvedenieStroja';
import { Dph } from '../components/Dph'; /* .893 DPH */
import Hra2048 from '../components/Hra2048';
import Aktualizacie from '../components/Aktualizacie';
import CennikGeis from '../components/CennikGeis';

const cis = (v) => (v === null || v === undefined ? '—' : String(v));
const pct = (v) => (v === null || v === undefined ? '—' : `${String(v).replace('.', ',')} %`);
const n0 = (v) => Number(v) || 0;

/* ------------------------------------------------------------ .888 farby */

/* Ikona v hlavičke karty – farebný štvorček. */
const IKONA_TON = {
  slate: 'bg-slate-100 text-slate-600 ring-slate-200',
  sky: 'bg-sky-100 text-sky-700 ring-sky-200',
  emerald: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  amber: 'bg-amber-100 text-amber-700 ring-amber-200',
  rose: 'bg-rose-100 text-rose-700 ring-rose-200',
  violet: 'bg-violet-100 text-violet-700 ring-violet-200',
  indigo: 'bg-indigo-100 text-indigo-700 ring-indigo-200',
};

/* Dlaždica s číslom vnútri karty. `brand` (pôvodný „aktuálne") = sky. */
const DLAZDICA = {
  slate: { box: 'from-slate-50 to-white ring-slate-200', cislo: 'text-slate-800', popis: 'text-slate-500' },
  sky: { box: 'from-sky-50 to-white ring-sky-200', cislo: 'text-sky-800', popis: 'text-sky-700' },
  emerald: { box: 'from-emerald-50 to-white ring-emerald-200', cislo: 'text-emerald-700', popis: 'text-emerald-700' },
  amber: { box: 'from-amber-50 to-white ring-amber-200', cislo: 'text-amber-700', popis: 'text-amber-800' },
  rose: { box: 'from-rose-50 to-white ring-rose-200', cislo: 'text-rose-700', popis: 'text-rose-700' },
  violet: { box: 'from-violet-50 to-white ring-violet-200', cislo: 'text-violet-800', popis: 'text-violet-700' },
};
DLAZDICA.brand = DLAZDICA.sky;

/* Číslo je „prázdne", keď je nula alebo nič – vtedy dlaždica nesvieti. */
const jePrazdne = (h) => h === null || h === undefined || h === '—' || h === '0'
  || h === 0 || /^0(,00)?\s*(€|%)?$/.test(String(h).replace(/ /g, ' ').trim());

/* ------------------------------------------------------------ stavebné diely */

/** Rám karty. Nadpis, odkaz „celá sekcia" a miesto na obsah. */
function Karta({ ikona: Ikona, nazov, kam, kamPopis = 'otvoriť', vpravo, children, chyba, farba = 'slate', skupina }) {
  return (
    /* .888 zaoblenejšia karta, farebná ikona, jemný zdvih pri prejdení myšou */
    <div className="card flex h-full flex-col overflow-hidden !rounded-2xl transition-shadow duration-200 hover:shadow-md">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border-b border-slate-100 px-5 py-3 max-md:px-4">
        {Ikona && (
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${IKONA_TON[farba] || IKONA_TON.slate}`}>
            <Ikona size={16} />
          </span>
        )}
        <div className="min-w-0">
          {skupina && <div className="text-[10px] font-semibold uppercase leading-none tracking-wider text-slate-400">{skupina}</div>}
          <h2 className="font-semibold leading-tight text-slate-800">{nazov}</h2>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
          {vpravo}
          {kam && (
            <Link to={kam} className="group inline-flex items-center gap-0.5 rounded-full px-2 py-1 font-medium text-brand-700 transition hover:bg-brand-50">
              {kamPopis}
              <ArrowUpRight size={13} className="transition group-hover:-translate-y-px group-hover:translate-x-px" />
            </Link>
          )}
        </div>
      </div>
      {chyba ? (
        /* Karta, ktorá spadla, to povie pri sebe. Prázdne miesto by vyzeralo
           ako „nič tu nie je", čo je iná správa než „toto sa nepodarilo". */
        <p className="m-4 rounded-xl bg-rose-50 px-4 py-4 text-center text-sm text-rose-700 ring-1 ring-rose-200">{chyba}</p>
      ) : children}
    </div>
  );
}

/** Číslo s popisom. Veľké číslo je to, na čo sa človek pozerá ako prvé.
 *  .888 Je to farebná dlaždica – farbu dostane len vtedy, keď číslo nie je nula. */
function Cislo({ popis, hodnota, spodok, ton = 'slate', kam, extra }) {
  const prazdne = jePrazdne(hodnota);
  const t = prazdne ? DLAZDICA.slate : (DLAZDICA[ton] || DLAZDICA.slate);
  /* .888 dlhšia suma (napr. „21 400,00 €") dostane menšie písmo, nech sa zmestí do dlaždice */
  const dlzka = String(hodnota ?? '').length;
  const velkost = dlzka > 9 ? 'text-base' : dlzka > 6 ? 'text-xl' : 'text-2xl';
  const telo = (
    <>
      <div className={`text-[10.5px] font-semibold uppercase leading-tight tracking-wide ${t.popis}`}>{popis}</div>
      <div className="mt-1 flex items-center gap-2">
        <div className={`${velkost} whitespace-nowrap font-bold leading-tight tabular-nums ${prazdne ? 'text-slate-300' : t.cislo}`}>{hodnota}</div>
        {extra}
      </div>
      {spodok && <div className="mt-0.5 text-[11px] leading-snug text-slate-500">{spodok}</div>}
    </>
  );
  const trieda = `block min-w-0 rounded-xl bg-gradient-to-br px-3 py-2.5 ring-1 ${t.box}`;
  return kam
    ? <Link to={kam} className={`${trieda} transition hover:-translate-y-0.5 hover:shadow-md`}>{telo}</Link>
    : <div className={trieda}>{telo}</div>;
}

/* .888 Mriežka dlaždíc v karte – na telefóne vždy najviac dve vedľa seba. */
const Mriezka = ({ stlpce = 3, children }) => {
  const st = { 2: 'grid-cols-2', 3: 'grid-cols-3 max-md:grid-cols-2', 4: 'grid-cols-4 max-md:grid-cols-2' };
  return <div className={`grid gap-2.5 p-4 ${st[stlpce] || st[3]}`}>{children}</div>;
};

const Prazdno = ({ text }) => (
  /* .888 prázdny stav – jemná ikonka, nie len sivý text */
  <div className="flex flex-col items-center gap-2 px-5 py-8 text-center">
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 text-slate-300 ring-1 ring-slate-100">
      <Sparkles size={18} />
    </span>
    <p className="text-sm text-slate-400">{text}</p>
  </div>
);

/* .888 Pätička karty – drobný súhrn. */
const Pata = ({ children, ton }) => (
  <div className={`mt-auto border-t px-5 py-2 text-xs max-md:px-4 ${ton === 'amber'
    ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-slate-100 bg-slate-50/60 text-slate-500'}`}>
    {children}
  </div>
);

/* .888 Rozdelený pruh – podiel stavov (napr. čaká / objednané / naskladnené). */
const PRUH = { slate: 'bg-slate-300', sky: 'bg-sky-500', emerald: 'bg-emerald-500', amber: 'bg-amber-400', rose: 'bg-rose-500', violet: 'bg-violet-500' };
function Pruh({ casti }) {
  const spolu = casti.reduce((s, c) => s + n0(c.n), 0);
  if (!spolu) return null;
  return (
    <div className="px-4 pb-4">
      <div className="flex h-2 overflow-hidden rounded-full bg-slate-100">
        {casti.filter((c) => n0(c.n) > 0).map((c) => (
          <div key={c.popis} className={`${PRUH[c.ton] || PRUH.slate} transition-all`}
            style={{ width: `${(n0(c.n) / spolu) * 100}%` }} title={`${c.popis}: ${c.n}`} />
        ))}
      </div>
    </div>
  );
}

/* .888 Malý graf priebehu (bez knižnice) – z čísel, ktoré nástenka už má. */
function Iskra({ hodnoty, className = '' }) {
  const h = (hodnoty || []).map(n0);
  if (h.length < 2) return null;
  const max = Math.max(...h, 1);
  const body = h.map((v, i) => [(i / (h.length - 1)) * 100, 28 - (v / max) * 24]);
  const ciara = body.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className={className} aria-hidden="true">
      <path d={`${ciara} L100,30 L0,30 Z`} fill="currentColor" opacity="0.12" />
      <path d={ciara} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/* .888 Kruh s percentom (úspešnosť). */
function Kruh({ hodnota, farba = 'text-emerald-500', size = 34 }) {
  if (hodnota === null || hodnota === undefined) return null;
  const v = Math.max(0, Math.min(100, Number(hodnota) || 0));
  const r = 15.5;
  const o = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" className="shrink-0 -rotate-90" aria-hidden="true">
      <circle cx="18" cy="18" r={r} fill="none" className="stroke-slate-100" strokeWidth="4" />
      <circle cx="18" cy="18" r={r} fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round"
        className={farba} strokeDasharray={`${(v / 100) * o} ${o}`} />
    </svg>
  );
}

/* .888 Riadok zoznamu – farebný pásik vľavo, keď je niečo po termíne. */
const riadok = (ton) => `flex items-center gap-3 border-l-[3px] px-5 py-2.5 transition-colors hover:bg-slate-50 max-md:px-4 ${
  ton === 'rose' ? 'border-l-rose-400 bg-rose-50/50'
    : ton === 'amber' ? 'border-l-amber-400 bg-amber-50/50'
      : ton === 'indigo' ? 'border-l-indigo-400 bg-indigo-50/60'
        : ton === 'sky' ? 'border-l-sky-400 bg-sky-50/50'
          : ton === 'emerald' ? 'border-l-emerald-400 bg-emerald-50/50' : 'border-l-transparent'}`;

/* ------------------------------------------------------------------ karty */

function MojDen({ d, skupina, obnov }) {
  const toast = useToast();
  const [robim, setRobim] = useState(null);
  /* .922 úlohu sa dá odškrtnúť rovno z nástenky */
  const hotovo = async (u) => {
    setRobim(u.id);
    try { await api.put(`/api/leady/uloha/${u.id}`, { done: true }); toast.success(`Splnené: ${u.nazov}`); await obnov?.(); }
    catch (e) { toast.error(e.message); }
    finally { setRobim(null); }
  };
  return (
    <Karta ikona={CalendarClock} farba="sky" skupina={skupina} nazov="Môj deň" kam="/leady" kamPopis="lead management"
      chyba={d?.chyba}
      vpravo={d?.vlakna ? (
        <Link to="/schranka" className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 font-medium text-sky-700 ring-1 ring-sky-200 hover:bg-sky-100">
          <Inbox size={12} /> {d.vlakna} vlákien pre mňa
        </Link>
      ) : null}>
      {!d?.ulohy?.length ? (
        <Prazdno text="Na dnes nemáš žiadnu úlohu. To je dobrá správa." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {d.ulohy.map((u) => (
            <li key={u.id} className={riadok(u.po_termine ? 'rose' : null)}>
              <button type="button" title="Hotovo" disabled={robim === u.id} onClick={() => hotovo(u)}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 ring-1 ring-slate-300 transition hover:bg-emerald-50 hover:text-emerald-600 hover:ring-emerald-400 disabled:opacity-50">
                <Check size={14} />
              </button>
              <div className="min-w-0 flex-1">
                <Link to={u.lead_id ? `/leady/${u.lead_id}` : '/leady'}
                  className="text-sm font-medium text-slate-800 hover:text-brand-700">
                  {u.nazov}
                </Link>
                {u.komu && <div className="truncate text-xs text-slate-500">{u.komu}</div>}
              </div>
              {u.telefon && (
                <a href={`tel:${String(u.telefon).replace(/\s/g, '')}`} title={`Zavolať ${u.telefon}`}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100">
                  <Phone size={13} />
                </a>
              )}
              <div className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${u.po_termine ? 'bg-rose-100 font-medium text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
                {u.termin ? dtSk(u.termin) : 'bez termínu'}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Karta>
  );
}

/* .888 Veľká dlaždica hlavných čísel – ikona, farba, prípadne malý graf. */
const VELKA = {
  sky: { box: 'from-sky-500 to-sky-600', tien: 'shadow-sky-500/20' },
  violet: { box: 'from-violet-500 to-fuchsia-600', tien: 'shadow-violet-500/20' },
  amber: { box: 'from-amber-400 to-orange-500', tien: 'shadow-amber-500/20' },
  slate: { box: 'from-slate-600 to-slate-800', tien: 'shadow-slate-500/20' },
};
function VelkaDlazdica({ popis, hodnota, spodok, Ik, farba = 'slate', kam, iskra }) {
  const f = VELKA[farba] || VELKA.slate;
  return (
    <Link to={kam} data-farby
      className={`group relative block overflow-hidden rounded-2xl bg-gradient-to-br p-4 text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl ${f.box} ${f.tien}`}>
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10" />
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-white/80">{popis}</div>
          <div className="mt-1 text-3xl font-bold leading-none tabular-nums max-md:text-2xl">{hodnota}</div>
          {spodok && <div className="mt-1.5 truncate text-xs font-medium text-white/85">{spodok}</div>}
        </div>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/30 max-md:h-8 max-md:w-8">
          <Ik size={20} />
        </span>
      </div>
      {iskra && <Iskra hodnoty={iskra} className="mt-2 h-8 w-full text-white" />}
    </Link>
  );
}

function Cisla({ d, vsetko }) {
  if (d?.chyba) return <Karta ikona={TrendingUp} nazov="Objednávky a ponuky" chyba={d.chyba} />;
  /* Graf mesiacov sa požičia z karty „Objednávky po mesiacoch", ak je na nástenke. */
  const iskra = (vsetko?.obrat?.mesiace || []).map((m) => m.s);
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 max-md:grid-cols-2 max-md:gap-2.5">
      <VelkaDlazdica popis="Otvorené objednávky" hodnota={cis(d?.otvorene?.c)} Ik={ShoppingCart}
        spodok={`${money(d?.otvorene?.s || 0)} s DPH`} farba="sky" kam="/objednavky" />
      <VelkaDlazdica popis="Tento mesiac" hodnota={cis(d?.mesiac?.c)} Ik={Wallet}
        spodok={`${money(d?.mesiac?.s || 0)} s DPH`} farba="violet" kam="/objednavky"
        iskra={iskra.length > 1 ? iskra : null} />
      <VelkaDlazdica popis="Ponuky v riešení" hodnota={cis(d?.ponuky?.c)} Ik={FileText}
        spodok={`${money(d?.ponuky?.s || 0)} s DPH`} farba="amber" kam="/ponuky" />
      <VelkaDlazdica popis="Zákazníci / tovar" hodnota={cis(d?.zakaznikov)} Ik={Users}
        spodok={`${cis(d?.tovaru)} položiek v katalógu`} farba="slate" kam="/zakaznici" />
    </div>
  );
}

function EshopDnes({ d, skupina }) {
  return (
    <Karta ikona={ShoppingCart} farba="emerald" skupina={skupina} nazov="E-shop dnes" kam="/na-objednanie" kamPopis="na objednanie"
      chyba={d?.chyba}>
      <Mriezka stlpce={3}>
        <Cislo popis="Dnes" hodnota={cis(d?.dnes?.pocet)} spodok={`${money(d?.dnes?.suma || 0)} s DPH`} ton="emerald" />
        <Cislo popis="Priemerná objednávka"
          hodnota={d?.dnes?.priemer !== null && d?.dnes?.priemer !== undefined
            ? money(d.dnes.priemer) : '—'}
          spodok={d?.tyzden?.priemer ? `týždeň ${money(d.tyzden.priemer)} · s DPH` : 's DPH'} ton="violet" />
        <Cislo popis="Čaká na platbu" hodnota={cis(d?.caka_na_platbu)}
          ton={d?.caka_na_platbu ? 'amber' : 'slate'}
          spodok="prevodom, ešte neprišlo" />
      </Mriezka>
      <Pata>
        Posledných 7 dní: <b className="font-semibold text-slate-700">{cis(d?.tyzden?.pocet)}</b> objednávok · <b className="font-semibold text-slate-700">{money(d?.tyzden?.suma || 0)}</b><Dph s />
      </Pata>
    </Karta>
  );
}

function NaObjednanie({ d, skupina }) {
  return (
    <Karta ikona={Truck} farba="amber" skupina={skupina} nazov="Na objednanie" kam="/na-objednanie" chyba={d?.chyba}>
      <Mriezka stlpce={4}>
        <Cislo popis="Čaká" hodnota={cis(d?.caka)} ton={d?.caka ? 'amber' : 'slate'} />
        <Cislo popis="Objednané" hodnota={cis(d?.objednane)} ton="sky" />
        <Cislo popis="Naskladnené" hodnota={cis(d?.naskladnene)} ton="emerald" />
        <Cislo popis="Nedá sa" hodnota={cis(d?.nedostupne)} ton={d?.nedostupne ? 'rose' : 'slate'} />
      </Mriezka>
      <Pruh casti={[
        { popis: 'Čaká', n: d?.caka, ton: 'amber' }, { popis: 'Objednané', n: d?.objednane, ton: 'sky' },
        { popis: 'Naskladnené', n: d?.naskladnene, ton: 'emerald' }, { popis: 'Nedá sa', n: d?.nedostupne, ton: 'rose' },
      ]} />
      <Pata>{cis(d?.objednavok)} objednávok v sekcii</Pata>
    </Karta>
  );
}

function BaliaciStol({ d, skupina }) {
  return (
    <Karta ikona={PackageCheck} farba="sky" skupina={skupina} nazov="Baliaci stôl" kam="/balenie" chyba={d?.chyba}>
      <Mriezka stlpce={3}>
        <Cislo popis="Rozbalené sedenia" hodnota={cis(d?.otvorene)}
          ton={d?.otvorene ? 'amber' : 'slate'} spodok="niekto pri nich stojí" />
        <Cislo popis="Zabalené dnes" hodnota={cis(d?.dnes)} ton="emerald" />
        <Cislo popis="Čaká na kuriéra" hodnota={cis(d?.caka_na_kuriera)} ton="sky"
          spodok="zabalené, neodovzdané" />
      </Mriezka>
      {(d?.stitok_chyba > 0 || d?.rucne_zasielky > 0) && (
        <div className="mt-auto flex flex-wrap gap-2 border-t border-slate-100 px-5 py-2.5 text-xs max-md:px-4">
          {d.stitok_chyba > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 font-medium text-rose-700 ring-1 ring-rose-200">
              <AlertTriangle size={12} /> {d.stitok_chyba} zásielok so zlyhaným štítkom
            </span>
          )}
          {d.rucne_zasielky > 0 && (
            <Link to="/zasielky" className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100">
              {d.rucne_zasielky} ručných zásielok čaká
            </Link>
          )}
        </div>
      )}
    </Karta>
  );
}

function Zasielky({ d, skupina }) {
  return (
    <Karta ikona={Package} farba="sky" skupina={skupina} nazov="Zásielky na ceste" kam="/zasielky" chyba={d?.chyba}>
      <Mriezka stlpce={4}>
        <Cislo popis="Na ceste" hodnota={cis(d?.na_ceste)} ton="sky" />
        <Cislo popis="Na výdajni" hodnota={cis(d?.na_mieste)} ton="amber" />
        <Cislo popis="Doručené" hodnota={cis(d?.dorucene)} ton="emerald" />
        <Cislo popis="Vrátené" hodnota={cis(d?.vratene)} ton={d?.vratene ? 'rose' : 'slate'} />
      </Mriezka>
      <Pruh casti={[
        { popis: 'Na ceste', n: d?.na_ceste, ton: 'sky' }, { popis: 'Na výdajni', n: d?.na_mieste, ton: 'amber' },
        { popis: 'Doručené', n: d?.dorucene, ton: 'emerald' }, { popis: 'Vrátené', n: d?.vratene, ton: 'rose' },
      ]} />
      {/* .633: štítok vytvorený, kuriér balík neprevzal – faktúra by inak neodišla. */}
      {!!d?.zaseknute?.length && (
        <div className="border-t border-amber-200 bg-gradient-to-br from-amber-50 to-white px-5 py-2.5 max-md:px-4">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
            <AlertTriangle size={13} /> Nepohli sa u kuriéra ({d.zaseknute.length}) – štítok je len vytvorený
          </div>
          <ul className="mt-1 space-y-0.5">
            {d.zaseknute.map((z) => (
              <li key={z.balenie_id} className="flex items-center gap-2 text-xs">
                <span className="font-medium text-slate-800">{z.code}</span>
                <span className="truncate text-slate-500">{z.kurier} {z.sledovacie}</span>
                <span className="ml-auto shrink-0 text-amber-700">{z.dni} prac. dni · {z.status_name}</span>
              </li>
            ))}
          </ul>
          <div className="mt-1 text-[11px] text-amber-700">
            Neostal balík na sklade? Nepreštítkoval ho kuriér? Kým je objednávka Odoslaná, faktúra zákazníkovi neodíde.
          </div>
        </div>
      )}
      {!!d?.vratene_zoznam?.length && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {d.vratene_zoznam.map((z) => (
            <li key={z.sledovacie || z.code} className={`${riadok('rose')} text-sm`}>
              <span className="font-medium text-slate-700">{z.code}</span>
              <span className="truncate text-xs text-slate-500">
                {z.kurier} {z.sledovacie}
              </span>
              <span className="ml-auto shrink-0 text-xs font-medium text-rose-600">
                {z.stav_zasielky_text || 'vrátená'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Karta>
  );
}

function Reklamacie({ d, skupina }) {
  return (
    <Karta ikona={AlertTriangle} farba="rose" skupina={skupina} nazov="Reklamácie" kam="/reklamacie" chyba={d?.chyba}
      vpravo={d?.nad_30_dni ? (
        <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700 ring-1 ring-red-200">
          {d.nad_30_dni} nad 30 dní
        </span>
      ) : null}>
      <Mriezka stlpce={2}>
        <Cislo popis="Otvorené" hodnota={cis(d?.otvorene)} ton="sky" />
        <Cislo popis="U nás nad 30 dní" hodnota={cis(d?.nad_30_dni)}
          ton={d?.nad_30_dni ? 'rose' : 'slate'} spodok="zákonná lehota" />
      </Mriezka>
      {!!d?.najstarsie?.length && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {d.najstarsie.map((c) => (
            <li key={c.id} className={`${riadok(c.dni > 30 ? 'rose' : null)} text-sm`}>
              <Link to={`/reklamacie/${c.id}`} className="font-medium text-slate-700 hover:text-brand-700">
                {c.claim_no}
              </Link>
              <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs ${c.dni > 30 ? 'bg-rose-100 font-medium text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
                {c.dni} {c.dni === 1 ? 'deň' : c.dni < 5 ? 'dni' : 'dní'} u nás
              </span>
            </li>
          ))}
        </ul>
      )}
    </Karta>
  );
}

function Servis({ d, skupina }) {
  return (
    <Karta ikona={Wrench} farba="amber" skupina={skupina} nazov="Externý servis" kam="/reklamacie" chyba={d?.chyba}
      vpravo={d?.ceka ? (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800 ring-1 ring-amber-200">
          {d.ceka} na schválenie
        </span>
      ) : null}>
      {!d?.zoznam?.length ? <Prazdno text="Servis zatiaľ nič neposlal." /> : (
        <ul className="divide-y divide-slate-100">
          {d.zoznam.map((z) => {
            const caka = z.tech_state === 'ceka';
            return (
              <li key={z.id} className={riadok(caka ? 'amber' : null)}>
                <div className="min-w-0 flex-1">
                  <Link to={`/servis/${z.id}`} className="text-sm font-medium text-slate-800 hover:text-brand-700">
                    {z.claim_no}
                  </Link>
                  {!!z.pakc && (
                    <span className="ml-1.5 rounded bg-violet-100 px-1 text-[10px] font-bold text-violet-800">PAKC</span>
                  )}
                  <div className="truncate text-xs text-slate-500">{z.stroj}</div>
                </div>
                <div className="shrink-0 text-right">
                  {z.tech_price ? <div className="text-sm font-medium text-slate-700">{money(z.tech_price)}</div> : null}
                  <div className={`text-xs ${caka ? 'font-medium text-amber-700' : 'text-emerald-600'}`}>
                    {caka ? 'čaká na schválenie' : 'hotové'}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Karta>
  );
}

function Zmluvy({ d, skupina }) {
  /* Server to posiela ako `prehliadky` – ten kľúč rozhoduje. */
  const items = d?.prehliadky || [];
  return (
    <Karta ikona={FileSignature} farba="indigo" skupina={skupina} nazov="Servisné zmluvy" kam="/servisne-zmluvy" chyba={d?.chyba}
      vpravo={d?.aktivnych !== undefined ? (
        <span className="text-slate-400">{d.aktivnych} aktívnych · {money(d.rocne || 0)} ročne bez DPH</span>
      ) : null}>
      {!items.length ? <Prazdno text="Žiadna prehliadka sa v najbližších dňoch neblíži." /> : (
        <ul className="divide-y divide-slate-100">
          {items.map((v) => (
            <li key={v.id} className={riadok(v.dni < 0 ? 'rose' : null)}>
              <div className="min-w-0 flex-1">
                <Link to={`/servisne-zmluvy/${v.contract_id}`}
                  className="text-sm font-medium text-slate-800 hover:text-brand-700">
                  {v.customer_company || v.customer_name || v.number}
                </Link>
                <div className="truncate text-xs text-slate-500">{v.stroj}</div>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                v.dni < 0 ? 'bg-red-100 text-red-700'
                  : v.dni <= 7 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                {v.dni < 0 ? `mešká ${Math.abs(v.dni)} dní` : v.dni === 0 ? 'dnes' : `o ${v.dni} dní`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Karta>
  );
}

function Ponuky({ d, skupina }) {
  return (
    <Karta ikona={AlertCircle} farba="amber" skupina={skupina} nazov="Ponuky čakajúce na odpoveď" kam="/ponuky" chyba={d?.chyba}>
      {!d?.zoznam?.length ? <Prazdno text="Žiadne odoslané ponuky." /> : (
        <ul className="divide-y divide-slate-100">
          {d.zoznam.map((q) => {
            const poPlatnosti = q.valid_until && q.valid_until < today();
            return (
              <li key={q.id} className={riadok(poPlatnosti ? 'rose' : null)}>
                <div className="min-w-0 flex-1">
                  <Link to={`/ponuky/${q.id}`} className="text-sm font-medium text-slate-800 hover:text-brand-700">
                    {q.number}
                  </Link>
                  <div className="truncate text-xs text-slate-500">{q.firma || q.meno || '—'}</div>
                </div>
                <div className="shrink-0 text-sm font-semibold tabular-nums text-slate-700">{money(q.total_gross)}<Dph s /></div>
                <div className={`w-24 shrink-0 text-right text-xs ${poPlatnosti ? 'font-medium text-red-600' : 'text-slate-500'}`}>
                  {poPlatnosti ? 'expirovala ' : 'platí do '}{dateSk(q.valid_until)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Karta>
  );
}

/* .669 Predvedenia strojov – dnešné svietia. */
function Predvedenia({ d, skupina }) {
  const dnes = String(d?.teraz || '').slice(0, 10);
  return (
    <Karta ikona={CalendarClock} farba="indigo" skupina={skupina} nazov="Predvedenia strojov" kam="/ponuky" kamPopis="ponuky" chyba={d?.chyba}>
      {!d?.zoznam?.length ? <Prazdno text="Na najbližšie 3 týždne nie je dohodnuté žiadne predvedenie." /> : (
        <ul className="divide-y divide-slate-100">
          {d.zoznam.map((p) => {
            const jeDnes = String(p.zaciatok).slice(0, 10) === dnes;
            return (
              <li key={p.id} className={riadok(jeDnes ? 'indigo' : null)}>
                <div className={`w-28 shrink-0 text-sm max-md:w-20 ${jeDnes ? 'font-bold text-indigo-800' : 'text-slate-700'}`}>
                  {jeDnes ? 'DNES' : casPredvedenia(p.zaciatok).split(' ').slice(0, 3).join(' ')}
                  <div className="text-xs font-normal text-slate-500">{String(p.zaciatok).slice(11, 16)}{p.koniec ? `–${String(p.koniec).slice(11, 16)}` : ''}</div>
                </div>
                <div className="min-w-0 flex-1">
                  {p.quote_id ? (
                    <Link to={`/ponuky/${p.quote_id}`} className="text-sm font-medium text-slate-800 hover:text-brand-700">{p.firma || p.meno || p.ponuka_cislo}</Link>
                  ) : <span className="text-sm font-medium text-slate-800">{p.firma || p.meno || '—'}</span>}
                  <div className="truncate text-xs text-slate-500">{[p.stroj, p.miesto].filter(Boolean).join(' · ') || p.ponuka_cislo}</div>
                </div>
                {p.telefon && <a href={`tel:${p.telefon}`} className="shrink-0 text-xs text-slate-500 hover:text-brand-700">{p.telefon}</a>}
              </li>
            );
          })}
        </ul>
      )}
    </Karta>
  );
}

const MESIACE_SK = ['jan', 'feb', 'mar', 'apr', 'máj', 'jún', 'júl', 'aug', 'sep', 'okt', 'nov', 'dec'];

function Obrat({ d, skupina }) {
  const mesiace = d?.mesiace || [];
  const max = Math.max(...mesiace.map((r) => r.s), 1);
  const spolu = mesiace.reduce((s, r) => s + n0(r.s), 0);
  return (
    <Karta ikona={BarChart3} farba="violet" skupina={skupina} nazov="Objednávky po mesiacoch" kam="/objednavky" kamPopis="objednávky"
      chyba={d?.chyba}
      vpravo={mesiace.length ? <span className="font-semibold tabular-nums text-violet-700">{money(spolu)}<Dph s /></span> : null}>
      {!mesiace.length ? <Prazdno text="Zatiaľ žiadne objednávky." /> : (
        /* .888 stĺpce s prechodom, posledný (aktuálny) mesiac zvýraznený, pomocné čiary */
        <div className="relative px-5 pb-3 pt-6 max-md:px-3">
          <div className="pointer-events-none absolute inset-x-5 top-6 h-[120px] max-md:inset-x-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="absolute inset-x-0 border-t border-dashed border-slate-100" style={{ top: `${(i / 3) * 100}%` }} />
            ))}
          </div>
          <div className="relative flex items-end gap-1.5 max-md:gap-1" style={{ height: '150px' }}>
            {mesiace.map((r, i) => {
              const posledny = i === mesiace.length - 1;
              return (
                <div key={r.m} className="group flex min-w-0 flex-1 flex-col items-center justify-end gap-1" style={{ height: '100%' }}>
                  <div className="whitespace-nowrap rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-white opacity-0 shadow transition group-hover:opacity-100 max-md:hidden">
                    {money(r.s)} s DPH
                  </div>
                  <div className={`w-full rounded-t-md bg-gradient-to-t transition-all ${posledny
                    ? 'from-violet-600 to-fuchsia-500' : 'from-violet-300 to-violet-200 group-hover:from-violet-500 group-hover:to-violet-400'}`}
                    style={{ height: `${Math.max((r.s / max) * 120, 3)}px` }}
                    title={`${r.c} objednávok · ${money(r.s)} s DPH`} />
                  <div className={`text-[10px] ${posledny ? 'font-semibold text-violet-700' : 'text-slate-400'}`}>{r.m.slice(5)}/{r.m.slice(2, 4)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Karta>
  );
}

function HeurekaKarta({ d, skupina }) {
  return (
    <Karta ikona={Coins} farba="violet" skupina={skupina} nazov="Heureka za 7 dní" kam="/heureka" chyba={d?.chyba}>
      {!d?.dni ? (
        <Prazdno text="Za posledný týždeň nie je stiahnutý ani jeden deň." />
      ) : (
        <>
          <Mriezka stlpce={4}>
            <Cislo popis="Prekliky" hodnota={money(d.naklady)} spodok="bez DPH" ton="violet" />
            <Cislo popis="Tržba" hodnota={money(d.trzba)} ton="emerald"
              spodok={`${cis(d.objednavky)} objednávok · s DPH`} />
            <Cislo popis="PNO" hodnota={pct(d.pno)} ton={d.pno >= 15 ? 'amber' : 'slate'} />
            <Cislo popis="Bez objednávky" hodnota={money(d.zbytocne)}
              ton={d.zbytocne > 0 ? 'rose' : 'slate'} spodok="minuté nadarmo · bez DPH" />
          </Mriezka>
          <Pata>
            {d.dni} {d.dni === 1 ? 'stiahnutý deň' : 'stiahnutých dní'} · {cis(d.navstevy)} návštev
          </Pata>
        </>
      )}
    </Karta>
  );
}

function SkladPozor({ d, skupina }) {
  return (
    <Karta ikona={Percent} farba="rose" skupina={skupina} nazov="Pozor na sklad a ceny" kam="/dielna" kamPopis="cenová dielňa"
      chyba={d?.chyba}>
      <Mriezka stlpce={2}>
        <Cislo popis="Predáva sa pod nákupom" hodnota={cis(d?.pod_nakupom)}
          ton={d?.pod_nakupom ? 'rose' : 'slate'} />
        <Cislo popis="Bez nákupnej ceny" hodnota={cis(d?.bez_nakupnej)} ton="amber"
          spodok="maržu nevieme spočítať" />
      </Mriezka>
      {!!d?.zoznam?.length && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {d.zoznam.map((t) => (
            <li key={t.kod} className={`${riadok(null)} text-sm`}>
              <div className="min-w-0 flex-1">
                <div className="truncate text-slate-700">{t.nazov}</div>
                <div className="font-mono text-xs text-slate-400">{t.kod}</div>
              </div>
              <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-rose-200">
                {money(t.cena)} &lt; {money(t.nakup)} bez DPH
              </span>
            </li>
          ))}
        </ul>
      )}
    </Karta>
  );
}

function Prijem({ d, skupina }) {
  return (
    <Karta ikona={PackagePlus} farba="emerald" skupina={skupina} nazov="Príjem tovaru" kam="/prijem-tovaru" chyba={d?.chyba}>
      <Mriezka stlpce={3}>
        <Cislo popis="Rozrobené" hodnota={cis(d?.rozrobene)} ton={d?.rozrobene ? 'amber' : 'slate'} />
        <Cislo popis="Uzavreté" hodnota={cis(d?.uzavrete)} ton="emerald" />
        <Cislo popis="Neprenesené do POHODY" hodnota={cis(d?.neprenesene)}
          ton={d?.neprenesene ? 'rose' : 'slate'} />
      </Mriezka>
      {!!d?.zoznam?.length && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {d.zoznam.map((p) => (
            <li key={p.id} className={`${riadok(null)} text-sm`}>
              <Link to="/prijem-tovaru" className="min-w-0 flex-1 truncate text-slate-700 hover:text-brand-700">
                {p.nazov || p.dodavatel || `Príjem ${p.id}`}
              </Link>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{p.riadkov || 0} riadkov</span>
            </li>
          ))}
        </ul>
      )}
    </Karta>
  );
}

/* .945 Prehľad prijatých faktúr – čo je nové, v dodávke, v POHODE a čo nie. */
function FakturyDodavatelov({ d, skupina }) {
  return (
    <Karta ikona={Receipt} farba="amber" skupina={skupina} nazov="Faktúry od dodávateľov" kam="/prijem-faktur" chyba={d?.chyba}>
      <Mriezka stlpce={4}>
        <Cislo popis="Nové (čítajú sa)" hodnota={cis(d?.nove)} ton={d?.nove ? 'sky' : 'slate'} />
        <Cislo popis="Prečítané – čakajú" hodnota={cis(d?.precitane)} ton={d?.precitane ? 'amber' : 'slate'} />
        <Cislo popis="V dodávke" hodnota={cis(d?.v_dodavke)} ton={d?.v_dodavke ? 'amber' : 'slate'} />
        <Cislo popis="Chyba čítania" hodnota={cis(d?.chyba)} ton={d?.chyba ? 'rose' : 'slate'} />
      </Mriezka>
      <Mriezka stlpce={3}>
        <Cislo popis="Nie sú v POHODE" hodnota={cis(d?.mimo_pohody)} ton={d?.mimo_pohody ? 'rose' : 'emerald'} />
        <Cislo popis={`Tento mesiac (${cis(d?.mesiac_v_pohode)} v POHODE)`} hodnota={cis(d?.mesiac_pocet)} ton="violet" />
        <Cislo popis="Dobropisy mimo POHODY" hodnota={cis(d?.dobropisy_mimo)} ton={d?.dobropisy_mimo ? 'rose' : 'slate'} />
      </Mriezka>
      {!!d?.zoznam?.length && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {d.zoznam.map((f) => (
            <li key={f.id} className={`${riadok(null)} text-sm`}>
              <Link to="/prijem-faktur" className="min-w-0 flex-1 truncate text-slate-700 hover:text-brand-700">
                {f.dodavatel || '—'} <span className="font-mono text-xs text-slate-500">{f.cislo}</span>
                {f.dobropis && <span className="ml-1 rounded bg-rose-100 px-1 text-[10px] font-semibold text-rose-700">dobropis</span>}
              </Link>
              <span className="shrink-0 text-xs text-slate-400">{f.datum ? String(f.datum).slice(0, 10).split('-').reverse().join('. ') : ''}</span>
              <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700" title={f.dovod || ''}>nie je v POHODE</span>
            </li>
          ))}
        </ul>
      )}
      {(d?.bez_prekladu > 0 || d?.neodovzdane > 0) && (
        <Pata>
          {[d.bez_prekladu ? `${d.bez_prekladu} bez prekladu` : null, d.neodovzdane ? `${d.neodovzdane} neodovzdaných účtovníčke` : null].filter(Boolean).join(' · ')}
        </Pata>
      )}
    </Karta>
  );
}

function Schranka({ d, skupina }) {
  return (
    <Karta ikona={Inbox} farba="sky" skupina={skupina} nazov="Schránka" kam="/schranka" chyba={d?.chyba}>
      <Mriezka stlpce={4}>
        <Cislo popis="Nové" hodnota={cis(d?.nove)} ton={d?.nove ? 'rose' : 'slate'} />
        <Cislo popis="Otvorené" hodnota={cis(d?.otvorene)} ton="sky" />
        <Cislo popis="Bez zodpovedného" hodnota={cis(d?.bez_zodpovedneho)}
          ton={d?.bez_zodpovedneho ? 'amber' : 'slate'} />
        <Cislo popis="Moje" hodnota={cis(d?.moje)} ton="brand" />
      </Mriezka>
      {d?.stare > 0 && (
        <Pata ton="amber">
          {d.stare} vlákien sa týždeň nepohlo — zákazník si to pamätá.
        </Pata>
      )}
    </Karta>
  );
}


/**
 * Môj predaj.
 *
 * Toto je to, čo bývalo samostatnou sekciou „Prehľad predaja" – a jediná
 * časť, ktorú nástenka predtým nevedela: **ako sa darí konkrétnemu človeku**.
 *
 * Prepínač kolegu je vnútri karty, nie v nastavení nástenky: je to otázka
 * „koho sa práve pýtam", nie „čo tu chcem mať". Zoznam mien chodí zo servera
 * a ten ho pošle len administrátorovi.
 */
function MojPredaj({ d, volby, onVolba, skupina }) {
  const kolegovia = d?.kolegovia || [];
  return (
    <Karta ikona={TrendingUp} farba="violet" skupina={skupina} nazov="Môj predaj" kam="/ponuky" kamPopis="cenové ponuky"
      chyba={d?.chyba}
      vpravo={kolegovia.length > 1 ? (
        <select className="input !mt-0 !w-auto !rounded-full !py-0.5 text-xs"
          value={String(volby?.predaj_kto || '')}
          onChange={(e) => onVolba({ predaj_kto: e.target.value })}>
          <option value="">Môj prehľad</option>
          {kolegovia.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      ) : null}>
      <Mriezka stlpce={4}>
        <Cislo popis="Ponuky tento mesiac" hodnota={money(d?.ponuky?.suma || 0)}
          spodok={`${cis(d?.ponuky?.pocet)} ponúk · s DPH`} ton="violet" />
        <Cislo popis="Z toho prijaté" hodnota={money(d?.prijate?.suma || 0)}
          spodok={`${cis(d?.prijate?.pocet)} ponúk · s DPH`} ton="emerald" />
        <Cislo popis="Úspešnosť" hodnota={d?.uspesnost === null || d?.uspesnost === undefined
          ? '—' : `${d.uspesnost} %`} spodok="zo sumy, nie z počtu" ton="brand"
          extra={<Kruh hodnota={d?.uspesnost} farba="text-sky-500" size={28} />} />
        <Cislo popis="Objednávky" hodnota={money(d?.objednavky?.suma || 0)}
          spodok={`${cis(d?.objednavky?.pocet)} objednávok · s DPH`} ton="violet" />
      </Mriezka>
      <Pata>
        {d?.kto?.name ? `${d.kto.name} · ` : ''}{cis(d?.zakaznici)} zákazníkov s ponukou za rok
      </Pata>
    </Karta>
  );
}

function MojeLeady({ d, skupina }) {
  const stavy = d?.stavy || [];
  const max = Math.max(1, ...stavy.map((st) => n0(d?.podla_stavu?.[st.key])));
  return (
    <Karta ikona={Target} farba="indigo" skupina={skupina} nazov="Moje leady" kam="/leady" chyba={d?.chyba}
      vpravo={d?.nepridelene > 0 ? (
        <Link to="/leady?majitel=nikto" className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100">
          {d.nepridelene} bez majiteľa
        </Link>
      ) : null}>
      {!stavy.length ? <Prazdno text="Zatiaľ žiadne leady." /> : (
        <div className="space-y-1 px-4 py-3">
          {stavy.map((st) => {
            const n = n0(d.podla_stavu?.[st.key]);
            return (
              <Link key={st.key} to={`/leady?status=${st.key}`}
                className="group flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition hover:bg-slate-50">
                <span className={`badge w-28 shrink-0 justify-center ${st.cls}`}>{st.label}</span>
                {/* .888 pruh podľa počtu */}
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <span className="block h-full rounded-full bg-indigo-400 transition-all group-hover:bg-indigo-500"
                    style={{ width: `${(n / max) * 100}%` }} />
                </span>
                <span className="w-8 shrink-0 text-right font-semibold tabular-nums text-slate-700">
                  {d.podla_stavu?.[st.key] || 0}
                </span>
              </Link>
            );
          })}
        </div>
      )}
      <div className="mt-auto grid grid-cols-2 gap-2.5 border-t border-slate-100 p-4">
        <Cislo popis="získaných" ton="emerald"
          hodnota={d?.uspesnost === null || d?.uspesnost === undefined ? '—' : `${d.uspesnost} %`}
          extra={<Kruh hodnota={d?.uspesnost} size={26} />} />
        <Cislo popis="oslovení za 30 dní" hodnota={cis(d?.aktivit_30dni || 0)} ton="sky" />
      </div>
    </Karta>
  );
}

/**
 * Ponuky verzus objednávky.
 *
 * Dva stĺpce vedľa seba za každý mesiac: vľavo sivo to, čo sa ponúklo, vpravo
 * červeno to, čo z toho naozaj vyšlo. Rozdiel medzi nimi je celý zmysel grafu
 * — jeden stĺpec by hovoril len o tom, koľko sa toho napísalo.
 */
function PredajGraf({ d, skupina }) {
  const mesiace = d?.mesiace || [];
  const max = Math.max(1, ...mesiace.map((m) => Math.max(m.ponuky, m.objednavky)));
  const popis = (m) => {
    const [r, mm] = String(m).split('-');
    return `${MESIACE_SK[Number(mm) - 1] || m} ${String(r).slice(2)}`;
  };
  return (
    <Karta ikona={CheckCircle2} farba="emerald" skupina={skupina} nazov="Ponuky verzus objednávky" chyba={d?.chyba}>
      {!mesiace.length ? <Prazdno text="Zatiaľ žiadne údaje." /> : (
        <>
          <div className="flex h-44 items-end gap-2 px-5 pt-4 max-md:gap-1 max-md:px-3">
            {mesiace.map((m) => (
              <div key={m.m} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                <div className="flex w-full items-end justify-center gap-0.5" style={{ height: '120px' }}>
                  <div className="w-1/2 rounded-t bg-gradient-to-t from-slate-300 to-slate-200"
                    style={{ height: `${(m.ponuky / max) * 100}%` }}
                    title={`Ponuky ${money(m.ponuky)} s DPH`} />
                  <div className="w-1/2 rounded-t bg-gradient-to-t from-brand-700 to-brand-500"
                    style={{ height: `${(m.objednavky / max) * 100}%` }}
                    title={`Objednávky ${money(m.objednavky)} s DPH`} />
                </div>
                <span className="truncate text-[10px] text-slate-400">{popis(m.m)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-4 px-5 pb-3 pt-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-slate-300" /> vystavené ponuky
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-brand-600" /> objednávky
            </span>
          </div>
        </>
      )}
      {!!d?.top_tovar?.length && (
        <div className="border-t border-slate-100 px-5 py-3 max-md:px-4">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <Package size={13} /> Najpredávanejší tovar za rok
          </div>
          <ol className="space-y-1.5">
            {d.top_tovar.map((t, i) => (
              <li key={t.code || t.name || i} className="flex items-baseline gap-2 text-sm">
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center self-center rounded-full text-[10px] font-bold ${i === 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                <span className="min-w-0 flex-1 truncate text-slate-700" title={t.name}>{t.name}</span>
                <span className="shrink-0 text-xs text-slate-400">{Number(t.mnozstvo)} ks</span>
                <span className="shrink-0 font-semibold tabular-nums text-slate-800">{money(t.suma)}<Dph s /></span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Karta>
  );
}

/* ------------------------------------------------ .922 obchodný zástupca */

const AKCIE = [
  { to: '/ponuky/nova', label: 'Nová ponuka', Ik: FilePlus2, ton: 'from-brand-600 to-indigo-600 text-white ring-brand-500', hlavna: true },
  { to: '/leady?novy=1', label: 'Nový lead', Ik: UserPlus, ton: 'from-sky-50 to-white text-sky-800 ring-sky-200' },
  { to: '/leady', label: 'Lead management', Ik: Target, ton: 'from-indigo-50 to-white text-indigo-800 ring-indigo-200' },
  { to: '/objednavky', label: 'Objednávky', Ik: Package, ton: 'from-emerald-50 to-white text-emerald-800 ring-emerald-200' },
  { to: '/tovar', label: 'Hľadať tovar', Ik: Search, ton: 'from-slate-50 to-white text-slate-800 ring-slate-200' },
  { to: '/katalogy', label: 'Katalógy', Ik: BookOpen, ton: 'from-amber-50 to-white text-amber-800 ring-amber-200' },
  { to: '/davkovanie', label: 'Dávkovanie chémie', Ik: FlaskConical, ton: 'from-teal-50 to-white text-teal-800 ring-teal-200' },
  { to: '/servisne-zmluvy', label: 'Servisné zmluvy', Ik: FileSignature, ton: 'from-violet-50 to-white text-violet-800 ring-violet-200' },
];

function ObchodAkcie({ skupina }) {
  const [q, setQ] = useState('');
  const nav = useNavigate();
  return (
    <Karta ikona={Sparkles} farba="indigo" skupina={skupina} nazov="Rýchle akcie">
      <div className="space-y-3 p-4">
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (q.trim()) nav(`/tovar?q=${encodeURIComponent(q.trim())}`); }}>
          <div className="relative min-w-0 flex-1">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input !pl-9" placeholder="Hľadať tovar – kód, názov, EAN…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <button className="btn-primary shrink-0" type="submit">Hľadať</button>
        </form>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {AKCIE.map((a) => (
            <Link key={a.to} to={a.to}
              className={`group flex items-center gap-2 rounded-xl bg-gradient-to-br px-3 py-2.5 text-sm font-semibold ring-1 transition hover:-translate-y-0.5 hover:shadow-md ${a.ton}`}>
              <a.Ik size={17} className="shrink-0" />
              <span className="min-w-0 truncate">{a.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </Karta>
  );
}

const odKontaktu = (t) => {
  if (!t) return 'ešte nekontaktovaný';
  const dni = Math.floor((Date.now() - new Date(String(t).replace(' ', 'T')).getTime()) / 86400000);
  return dni <= 0 ? 'kontakt dnes' : dni === 1 ? 'kontakt včera' : `kontakt pred ${dni} dňami`;
};

function KohoOslovit({ d, skupina }) {
  return (
    <Karta ikona={Phone} farba="emerald" skupina={skupina} nazov="Koho dnes osloviť" kam="/leady" kamPopis="všetky leady"
      chyba={d?.chyba}
      vpravo={d?.spolu ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{d.spolu} otvorených · {d.bez_kontaktu} bez kontaktu</span> : null}>
      {!d?.zoznam?.length ? <Prazdno text="Nemáš otvorené leady bez naplánovanej úlohy. Pridaj nový lead." /> : (
        <ul className="divide-y divide-slate-100">
          {d.zoznam.map((l) => (
            <li key={l.id} className={riadok(l.priorita >= 2 ? 'rose' : l.priorita === 1 ? 'amber' : null)}>
              <div className="min-w-0 flex-1">
                <Link to={`/leady/${l.id}`} className="block truncate text-sm font-medium text-slate-800 hover:text-brand-700">
                  {l.company || l.name || `Lead #${l.id}`}
                </Link>
                <div className="truncate text-xs text-slate-500">
                  {[l.company && l.name, l.city, l.stav, odKontaktu(l.last_contact_at)].filter(Boolean).join(' · ')}
                </div>
              </div>
              {l.email && (
                <a href={`mailto:${l.email}`} title={l.email}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-700 ring-1 ring-sky-200 hover:bg-sky-100">
                  <Mail size={13} />
                </a>
              )}
              {l.phone && (
                <a href={`tel:${String(l.phone).replace(/\s/g, '')}`} title={`Zavolať ${l.phone}`}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100">
                  <Phone size={13} />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </Karta>
  );
}

function MojePonuky({ d, skupina }) {
  const koncepty = d?.koncepty || [];
  const odoslane = d?.odoslane || [];
  return (
    <Karta ikona={FileText} farba="amber" skupina={skupina} nazov="Moje ponuky" kam="/ponuky" chyba={d?.chyba}
      vpravo={<Link to="/ponuky/nova" className="inline-flex items-center gap-1 rounded-full bg-brand-600 px-2.5 py-1 font-medium text-white hover:bg-brand-500"><Plus size={12} /> Nová</Link>}>
      {!koncepty.length && !odoslane.length ? <Prazdno text="Žiadna rozpracovaná ani odoslaná ponuka." /> : (
        <ul className="divide-y divide-slate-100">
          {koncepty.map((q) => (
            <li key={`k${q.id}`} className={riadok('sky')}>
              <div className="min-w-0 flex-1">
                <Link to={`/ponuky/${q.id}`} className="text-sm font-medium text-slate-800 hover:text-brand-700">{q.number}</Link>
                <div className="truncate text-xs text-slate-500">{q.firma || q.meno || 'bez zákazníka'}</div>
              </div>
              <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">rozpracovaná</span>
              <div className="w-24 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-700">{money(q.total_gross)}</div>
            </li>
          ))}
          {odoslane.map((q) => {
            const po = q.valid_until && q.valid_until < today();
            return (
              <li key={q.id} className={riadok(po ? 'rose' : null)}>
                <div className="min-w-0 flex-1">
                  <Link to={`/ponuky/${q.id}`} className="text-sm font-medium text-slate-800 hover:text-brand-700">{q.number}</Link>
                  <div className="truncate text-xs text-slate-500">
                    {q.firma || q.meno || '—'}
                    {q.opened_at ? <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700"><Eye size={11} /> otvoril</span> : <span className="ml-1 text-slate-400">· neotvoril</span>}
                  </div>
                </div>
                <div className={`shrink-0 text-xs ${po ? 'font-medium text-red-600' : 'text-slate-500'}`}>{po ? 'expirovala ' : 'do '}{dateSk(q.valid_until)}</div>
                <div className="w-24 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-700">{money(q.total_gross)}</div>
              </li>
            );
          })}
        </ul>
      )}
    </Karta>
  );
}

function MojeObjednavky({ d, skupina }) {
  return (
    <Karta ikona={ClipboardList} farba="emerald" skupina={skupina} nazov="Moje objednávky" kam="/objednavky" chyba={d?.chyba}
      vpravo={d?.otvorene?.c ? <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800 ring-1 ring-amber-200">{d.otvorene.c} otvorených · {money(d.otvorene.s)}</span> : null}>
      {!d?.zoznam?.length ? <Prazdno text="Zatiaľ žiadna objednávka. Vzniká prevodom z prijatej ponuky." /> : (
        <ul className="divide-y divide-slate-100">
          {d.zoznam.map((o) => (
            <li key={o.id} className={riadok(o.status === 'pripravena' ? 'emerald' : null)}>
              <div className="min-w-0 flex-1">
                <Link to={`/objednavky/${o.id}`} className="text-sm font-medium text-slate-800 hover:text-brand-700">{o.number}</Link>
                <div className="truncate text-xs text-slate-500">{o.firma || o.meno || o.contact_name || '—'} · {dateSk(o.order_date)}</div>
              </div>
              <StatusBadge status={o.status} />
              <div className="w-24 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-700">{money(o.total_gross)}</div>
            </li>
          ))}
        </ul>
      )}
    </Karta>
  );
}

/* Mapa kľúč → komponent. Karty označené `klient` si server nepočíta. */
const KOMPONENTY = {
  /* .888 zaoblenie rámu kalendára zladené s ostatnými kartami */
  den: () => <div className="[&>.card-p]:!rounded-2xl"><DenKalendar /></div>,
  moj_den: MojDen,
  obchod_akcie: ObchodAkcie,
  koho_oslovit: KohoOslovit,
  moje_ponuky: MojePonuky,
  moje_objednavky: MojeObjednavky,
  cisla: Cisla,
  eshop_dnes: EshopDnes,
  na_objednanie: NaObjednanie,
  baliaci_stol: BaliaciStol,
  zasielky: Zasielky,
  reklamacie: Reklamacie,
  servis: Servis,
  zmluvy: Zmluvy,
  ponuky: Ponuky,
  predvedenia: Predvedenia,
  obrat: Obrat,
  heureka: HeurekaKarta,
  sklad_pozor: SkladPozor,
  prijem: Prijem,
  faktury_dodavatelov: FakturyDodavatelov,
  schranka: Schranka,
  moj_predaj: MojPredaj,
  moje_leady: MojeLeady,
  predaj_graf: PredajGraf,
  geis: () => <div className="h-full [&>.card-p]:h-full [&>.card-p]:!rounded-2xl"><CennikGeis /></div>,
  aktualizacie: () => <div className="h-full [&>.card-p]:h-full [&>.card-p]:!rounded-2xl"><Aktualizacie /></div>,
  hra: () => <div className="h-full [&>.card-p]:h-full [&>.card-p]:!rounded-2xl"><Hra2048 /></div>,
};

/* ------------------------------------------------------- .888 hlavička */

/** Pozdrav podľa hodiny v predajni. */
function pozdrav() {
  const h = Number(new Date().toLocaleString('sk-SK', { timeZone: CAS_PASMO, hour: '2-digit', hour12: false }));
  if (h < 9) return 'Dobré ráno';
  if (h < 18) return 'Dobrý deň';
  return 'Dobrý večer';
}

/**
 * .888 Čo treba riešiť – poskladané len z čísel, ktoré karty na nástenke už
 * majú. Karta, ktorá na nástenke nie je, sem nič nepridá – nástenka sa nepýta
 * na nič navyše.
 */
function coRiesit(data) {
  const d = data || {};
  const p = [];
  const daj = (n, text, kam, ton, Ik) => { if (n0(n) > 0) p.push({ n: n0(n), text, kam, ton, Ik }); };
  daj((d.moj_den?.ulohy || []).filter((u) => u.po_termine).length, 'úloh po termíne', '/leady', 'rose', CalendarClock);
  daj(d.reklamacie?.nad_30_dni, 'reklamácií nad 30 dní', '/reklamacie', 'rose', AlertTriangle);
  daj(d.zasielky?.vratene, 'vrátených zásielok', '/zasielky', 'rose', Package);
  daj(d.baliaci_stol?.stitok_chyba, 'zlyhaných štítkov', '/balenie', 'rose', PackageCheck);
  daj(d.sklad_pozor?.pod_nakupom, 'pod nákupnou cenou', '/dielna', 'rose', Percent);
  daj(d.prijem?.neprenesene, 'príjmov neprenesených do POHODY', '/prijem-tovaru', 'rose', PackagePlus);
  daj((d.zmluvy?.prehliadky || []).filter((v) => v.dni < 0).length, 'zmeškaných prehliadok', '/servisne-zmluvy', 'rose', FileSignature);
  daj(d.schranka?.nove, 'nových vlákien v schránke', '/schranka', 'rose', Inbox);
  daj(d.servis?.ceka, 'servis na schválenie', '/reklamacie', 'amber', Wrench);
  daj(d.zasielky?.zaseknute?.length, 'zásielok sa nepohlo u kuriéra', '/zasielky', 'amber', Truck);
  daj(d.na_objednanie?.caka, 'čaká na objednanie', '/na-objednanie', 'amber', Truck);
  daj(d.eshop_dnes?.caka_na_platbu, 'čaká na platbu', '/na-objednanie', 'amber', Coins);
  daj((d.moje_ponuky?.odoslane || []).filter((q) => q.valid_until && q.valid_until < today()).length, 'mojich ponúk po platnosti', '/ponuky', 'amber', FileText);
  daj((d.ponuky?.zoznam || []).filter((q) => q.valid_until && q.valid_until < today()).length, 'expirovaných ponúk', '/ponuky', 'amber', FileText);
  daj(d.baliaci_stol?.rucne_zasielky, 'ručných zásielok čaká', '/zasielky', 'amber', Package);
  daj(d.schranka?.bez_zodpovedneho, 'vlákien bez zodpovedného', '/schranka', 'amber', Inbox);
  const dnes = String(d.predvedenia?.teraz || '').slice(0, 10);
  daj((d.predvedenia?.zoznam || []).filter((x) => dnes && String(x.zaciatok).slice(0, 10) === dnes).length, 'predvedení dnes', '/ponuky', 'sky', CalendarClock);
  return p;
}

const POZOR_TON = {
  rose: 'from-rose-50 to-white ring-rose-200 hover:ring-rose-300 [&_.n]:bg-rose-600 [&_.ik]:text-rose-600 text-rose-900',
  amber: 'from-amber-50 to-white ring-amber-200 hover:ring-amber-300 [&_.n]:bg-amber-500 [&_.ik]:text-amber-600 text-amber-900',
  sky: 'from-sky-50 to-white ring-sky-200 hover:ring-sky-300 [&_.n]:bg-sky-600 [&_.ik]:text-sky-600 text-sky-900',
};

function TrebaRiesit({ polozky }) {
  if (!polozky.length) return null;
  return (
    <div className="card overflow-hidden !rounded-2xl">
      <div className="flex items-center gap-2.5 border-b border-slate-100 bg-gradient-to-r from-rose-50 via-amber-50/60 to-white px-5 py-3 max-md:px-4">
        <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-rose-100 text-rose-700 ring-1 ring-rose-200">
          <AlertTriangle size={16} />
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full bg-rose-500 ring-2 ring-white" />
        </span>
        <h2 className="font-semibold text-slate-800">Treba riešiť</h2>
        <span className="rounded-full bg-rose-600 px-2 py-0.5 text-xs font-bold tabular-nums text-white">{polozky.length}</span>
      </div>
      <div className="grid grid-cols-2 gap-2.5 p-4 sm:grid-cols-3 xl:grid-cols-4 max-md:grid-cols-1 max-md:gap-2">
        {polozky.map((x) => (
          <Link key={x.text} to={x.kam}
            className={`group flex items-center gap-3 rounded-xl bg-gradient-to-br px-3 py-2.5 ring-1 transition hover:-translate-y-0.5 hover:shadow-md ${POZOR_TON[x.ton]}`}>
            <span className="n flex h-9 min-w-[2.25rem] shrink-0 items-center justify-center rounded-lg px-1.5 text-base font-bold tabular-nums text-white shadow-sm">{x.n}</span>
            <span className="min-w-0 flex-1 text-sm font-medium leading-tight">{x.text}</span>
            <ChevronRight size={16} className="ik shrink-0 opacity-60 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
          </Link>
        ))}
      </div>
    </div>
  );
}

/** Hlavné čísla do hlavičky – len z kariet, ktoré sú na nástenke. */
function hlavneCisla(data) {
  const d = data || {};
  const c = [];
  /* .888 Čísla z karty „Objednávky a ponuky" sú hneď pod hlavičkou vo veľkých
     dlaždiciach – sem idú len ako náhrada, aby sa to isté neopakovalo dvakrát. */
  if (d.eshop_dnes && !d.eshop_dnes.chyba) c.push({ popis: 'E-shop dnes', hodnota: cis(d.eshop_dnes.dnes?.pocet), pod: `${money(d.eshop_dnes.dnes?.suma || 0)} s DPH`, Ik: ShoppingCart, kam: '/na-objednanie' });
  if (d.moj_predaj && !d.moj_predaj.chyba) c.push({ popis: 'Ponuky tento mesiac', hodnota: money(d.moj_predaj.ponuky?.suma || 0), pod: `${cis(d.moj_predaj.ponuky?.pocet)} ponúk · s DPH`, Ik: TrendingUp, kam: '/ponuky' });
  if (d.baliaci_stol && !d.baliaci_stol.chyba) c.push({ popis: 'Zabalené dnes', hodnota: cis(d.baliaci_stol.dnes), pod: `${cis(d.baliaci_stol.caka_na_kuriera)} čaká na kuriéra`, Ik: PackageCheck, kam: '/balenie' });
  if (d.na_objednanie && !d.na_objednanie.chyba) c.push({ popis: 'Na objednanie', hodnota: cis(d.na_objednanie.objednavok), pod: `${cis(d.na_objednanie.caka)} čaká`, Ik: Truck, kam: '/na-objednanie' });
  if (d.cisla && !d.cisla.chyba) {
    c.push({ popis: 'Otvorené objednávky', hodnota: cis(d.cisla.otvorene?.c), pod: `${money(d.cisla.otvorene?.s || 0)} s DPH`, Ik: Package, kam: '/objednavky' });
    c.push({ popis: 'Tento mesiac', hodnota: cis(d.cisla.mesiac?.c), pod: `${money(d.cisla.mesiac?.s || 0)} s DPH`, Ik: Wallet, kam: '/objednavky' });
  }
  return c.slice(0, 3);
}

function Hlavicka({ stav, upravujem, setUpravujem, obnov, obnovujem, pozor }) {
  const meno = !stav.volby?.predaj_kto ? String(stav.data?.moj_predaj?.kto?.name || '').split(' ')[0] : '';
  const datum = new Date().toLocaleDateString('sk-SK', {
    timeZone: CAS_PASMO, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const cisla = hlavneCisla(stav.data);
  const rose = pozor.filter((x) => x.ton === 'rose').length;
  const sklo = 'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ring-1 transition max-md:flex-1 max-md:justify-center max-md:py-2';
  return (
    /* data-farby: v nočnom režime ostane hlavička vo svojich farbách */
    <div data-farby className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-950 to-brand-800 text-white shadow-lg">
      <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-brand-500/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-indigo-500/25 blur-3xl" />
      <div className="relative flex flex-wrap items-start gap-4 p-6 max-md:gap-3 max-md:p-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20 max-md:h-10 max-md:w-10">
          <LayoutDashboard size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-indigo-200">{pozdrav()}{meno ? `, ${meno}` : ''}</div>
          <h1 className="text-3xl font-bold leading-tight tracking-tight max-md:text-2xl">Prehľad</h1>
          <div className="mt-0.5 text-sm first-letter:uppercase text-white/80">{datum}</div>
          <p className="mt-1 text-xs text-white/55 max-md:hidden">
            {stav.vlastne
              ? 'Tvoja nástenka – poskladaná podľa teba.'
              : 'Predvolená zostava pre tvoju rolu. Dá sa prestaviť.'}
          </p>
        </div>
        <div className="flex items-center gap-2 max-md:w-full">
          <button className={`${sklo} bg-white/10 text-white ring-white/25 hover:bg-white/20 disabled:opacity-60`} onClick={obnov} disabled={obnovujem}>
            <RefreshCw size={14} className={obnovujem ? 'animate-spin' : ''} /> Obnoviť čísla
          </button>
          <button className={`${sklo} ${upravujem ? 'bg-white text-indigo-800 ring-white' : 'bg-white/10 text-white ring-white/25 hover:bg-white/20'}`}
            onClick={() => setUpravujem((x) => !x)}>
            <LayoutDashboard size={14} /> Upraviť nástenku
          </button>
        </div>
      </div>

      {/* Hlavné čísla – sklenené dlaždice */}
      <div className="relative grid grid-cols-2 gap-2.5 px-6 pb-6 md:grid-cols-4 max-md:px-4 max-md:pb-4">
        {cisla.map((c) => (
          <Link key={c.popis} to={c.kam}
            className="group rounded-xl bg-white/10 px-4 py-3 ring-1 ring-white/15 backdrop-blur transition hover:bg-white/15 max-md:px-3">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/70">
              <c.Ik size={13} /> <span className="truncate">{c.popis}</span>
            </div>
            <div className="mt-1 truncate text-2xl font-bold tabular-nums max-md:text-xl">{c.hodnota}</div>
            <div className="truncate text-xs text-white/65">{c.pod}</div>
          </Link>
        ))}
        <div className={`rounded-xl px-4 py-3 ring-1 max-md:px-3 ${pozor.length
          ? (rose ? 'bg-rose-500/25 ring-rose-300/40' : 'bg-amber-400/20 ring-amber-200/40')
          : 'bg-emerald-500/20 ring-emerald-300/40'}`}>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/75">
            {pozor.length ? <AlertTriangle size={13} /> : <CheckCircle2 size={13} />} Treba riešiť
          </div>
          <div className="mt-1 text-2xl font-bold tabular-nums max-md:text-xl">{pozor.length}</div>
          <div className="truncate text-xs text-white/70">
            {pozor.length ? (rose ? `${rose} problémových` : 'len čakajúce veci') : 'všetko v poriadku'}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- úprava */

/**
 * Režim úpravy.
 *
 * Mení sa **len v otvorenom paneli** a ukladá sa naraz. Nástenka, ktorá sa
 * prekresľuje pri každom kliknutí, sa nastavuje zle: človek nevidí výsledok,
 * kým neskončí.
 */
function Uprava({ karty, moje, onUloz, onZavri, onPredvolene }) {
  const [vyber, setVyber] = useState(moje);

  const pridaj = (k) => setVyber((s) => (s.includes(k) ? s : [...s, k]));
  const odober = (k) => setVyber((s) => s.filter((x) => x !== k));
  const posun = (k, smer) => setVyber((s) => {
    const i = s.indexOf(k);
    const j = i + smer;
    if (i < 0 || j < 0 || j >= s.length) return s;
    const n = [...s];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  const podlaKluca = useMemo(
    () => Object.fromEntries(karty.map((k) => [k.kluc, k])), [karty]);
  const dostupne = karty.filter((k) => !vyber.includes(k.kluc));
  const skupiny = [...new Set(dostupne.map((k) => k.skupina))];

  return (
    <div className="card-p space-y-4 !rounded-2xl ring-2 !ring-indigo-200">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200">
          <LayoutDashboard size={16} />
        </span>
        <b className="text-slate-800">Čo má byť na nástenke</b>
        <div className="ml-auto flex items-center gap-2">
          <button className="btn-secondary !py-1 !text-xs" onClick={onPredvolene}>
            <RotateCcw size={14} /> Vrátiť predvolené
          </button>
          <button className="btn-secondary !py-1 !text-xs" onClick={onZavri}>Zrušiť</button>
          <button className="btn-primary !py-1 !text-xs" onClick={() => onUloz(vyber)}>
            <Check size={14} /> Uložiť
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-2 text-xs uppercase tracking-wide text-slate-400">
            Na nástenke ({vyber.length}) — poradie zhora nadol
          </div>
          {!vyber.length ? (
            <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
              Nástenka je prázdna. Vyber si vpravo, čo na nej chceš mať.
            </p>
          ) : (
            <ul className="space-y-1">
              {vyber.map((k, i) => (
                <li key={k} className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-100 transition hover:bg-indigo-50/50">
                  <span className="w-5 shrink-0 text-center text-xs font-semibold tabular-nums text-slate-400">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800">
                      {podlaKluca[k]?.nazov || k}
                    </div>
                    <div className="truncate text-xs text-slate-500">{podlaKluca[k]?.popis}</div>
                  </div>
                  <button className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    disabled={i === 0} onClick={() => posun(k, -1)} title="Vyššie">
                    <ChevronUp size={16} />
                  </button>
                  <button className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    disabled={i === vyber.length - 1} onClick={() => posun(k, 1)} title="Nižšie">
                    <ChevronDown size={16} />
                  </button>
                  <button className="text-slate-400 hover:text-rose-600" onClick={() => odober(k)}
                    title="Dať preč">
                    <X size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="mb-2 text-xs uppercase tracking-wide text-slate-400">
            Čo sa dá pridať ({dostupne.length})
          </div>
          {!dostupne.length ? (
            <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
              Máš na nástenke všetko, čo ti systém vie ponúknuť.
            </p>
          ) : skupiny.map((sk) => (
            <div key={sk} className="mb-3">
              <div className="mb-1 text-xs font-semibold text-slate-500">{sk}</div>
              <ul className="space-y-1">
                {dostupne.filter((k) => k.skupina === sk).map((k) => (
                  <li key={k.kluc} className="flex items-center gap-2 rounded-xl px-3 py-2 ring-1 ring-slate-200 transition hover:bg-slate-50">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800">{k.nazov}</div>
                      <div className="text-xs text-slate-500">{k.popis}</div>
                    </div>
                    <button className="btn-secondary !py-1 !text-xs shrink-0"
                      onClick={() => pridaj(k.kluc)}>
                      <Plus size={14} /> Pridať
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- obrazovka */

export default function Dashboard() {
  const toast = useToast();
  const [stav, setStav] = useState(null);
  const [upravujem, setUpravujem] = useState(false);
  const [obnovujem, setObnovujem] = useState(false);

  const nacitaj = useCallback(() => api.get('/api/nastenka')
    .then(setStav)
    .catch((e) => { toast.error(e.message); setStav({ karty: [], moje: [], data: {} }); }), []);

  useEffect(() => { nacitaj(); }, [nacitaj]);

  const obnov = async () => {
    setObnovujem(true);
    try {
      const v = await api.get(`/api/nastenka/data?karty=${(stav?.moje || []).join(',')}`);
      setStav((s) => ({ ...s, data: v.data }));
    } catch (e) { toast.error(e.message); }
    finally { setObnovujem(false); }
  };

  /* Prepnutie kolegu v karte „Môj predaj". Uloží sa a rovno sa vrátia
     prepočítané čísla – prepínač, po ktorom chvíľu svietia staré čísla,
     vyzerá ako pokazený. */
  const zmenVolbu = async (nove) => {
    try {
      const v = await api.put('/api/nastenka/volby', nove);
      setStav((s) => ({ ...s, volby: v.volby, data: v.data }));
    } catch (e) { toast.error(e.message); }
  };

  const uloz = async (karty) => {
    try {
      await api.put('/api/nastenka', { karty });
      setUpravujem(false);
      await nacitaj();
      toast.success('Nástenka uložená.');
    } catch (e) { toast.error(e.message); }
  };

  const predvolene = async () => {
    try {
      await api.post('/api/nastenka/predvolene');
      setUpravujem(false);
      await nacitaj();
      toast.success('Nástenka je späť na predvolenej zostave.');
    } catch (e) { toast.error(e.message); }
  };

  if (!stav) return <Loading />;

  const podlaKluca = Object.fromEntries((stav.karty || []).map((k) => [k.kluc, k]));
  const moje = stav.moje || [];
  const pozor = coRiesit(stav.data);

  /* Karty sa ukladajú do dvoch stĺpcov, ale tie na celú šírku stĺpce prerušia.
     Preto sa zoznam delí na úseky: celá šírka stojí sama, ostatné sa párujú. */
  const useky = [];
  for (const kluc of moje) {
    const k = podlaKluca[kluc];
    if (!k) continue;
    const cela = k.sirka === 'cela';
    const posledny = useky[useky.length - 1];
    if (cela || !posledny || posledny.cela || posledny.karty.length >= 2) {
      useky.push({ cela, karty: [kluc] });
    } else {
      posledny.karty.push(kluc);
    }
  }

  /* .888 „Treba riešiť" ide hneď pod kalendár/hlavné čísla, teda za prvé
     úseky na celú šírku – tam, kam sa človek pozrie ako prvé. */
  let kamPozor = 0;
  while (kamPozor < useky.length && useky[kamPozor].cela) kamPozor += 1;

  const pas = <TrebaRiesit key="treba-riesit" polozky={pozor} />;

  return (
    <div className="space-y-5 max-md:space-y-4">
      <Hlavicka stav={stav} upravujem={upravujem} setUpravujem={setUpravujem}
        obnov={obnov} obnovujem={obnovujem} pozor={pozor} />

      {upravujem && (
        <Uprava karty={stav.karty} moje={moje} onUloz={uloz}
          onZavri={() => setUpravujem(false)} onPredvolene={predvolene} />
      )}

      {!moje.length && !upravujem && (
        <div className="card-p !rounded-2xl text-center text-sm text-slate-500">
          Nástenka je prázdna.{' '}
          <button className="text-brand-700 hover:underline" onClick={() => setUpravujem(true)}>
            Vyber si, čo na nej chceš mať.
          </button>
        </div>
      )}

      {!useky.length && pas}
      {useky.map((u, i) => [
        i === kamPozor && pas,
        <div key={`${u.karty[0]}-${i}`}
          className={u.cela ? '' : 'grid grid-cols-1 gap-5 lg:grid-cols-2 max-md:gap-4'}>
          {u.karty.map((kluc) => {
            const Komponent = KOMPONENTY[kluc];
            if (!Komponent) return null;
            return (
              <Komponent key={kluc} d={stav.data?.[kluc]} vsetko={stav.data}
                skupina={podlaKluca[kluc]?.skupina}
                volby={stav.volby} onVolba={zmenVolbu} obnov={obnov} />
            );
          })}
        </div>,
      ])}
      {useky.length > 0 && kamPozor >= useky.length && pas}
    </div>
  );
}
